import { useEffect, useRef, useState } from 'react';
import { useNavigation } from 'react-router';
import { useIsFetching } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { TeaPlusLogo } from './brand';

/** The Tea+ splash: Graphite with the logo gently breathing in the middle. */
export function Splash({ label = 'Loading Tea+' }: { label?: string }) {
  return (
    <div
      role="status"
      aria-label={label}
      className="fixed inset-0 z-[80] grid place-items-center bg-[radial-gradient(520px_360px_at_50%_46%,color-mix(in_oklab,var(--color-pink)_10%,transparent),transparent_70%)] bg-bg"
    >
      <TeaPlusLogo className="h-auto w-[176px] animate-[tp-splash-in_700ms_var(--ease-out-soft)_both,tp-breathe_2.6s_ease-in-out_700ms_infinite]" />
    </div>
  );
}

const fade = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] as const },
};

/** Covers slow page changes with the splash, so pages never freeze half-way. */
export function RouteLoader() {
  const busy = useNavigation().state !== 'idle';
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!busy) {
      setShow(false);
      return;
    }
    // Only for changes that take a moment; quick ones just happen.
    const t = setTimeout(() => setShow(true), 220);
    return () => clearTimeout(t);
  }, [busy]);
  return (
    <AnimatePresence>
      {show && (
        <motion.div key="route-loader" {...fade} className="fixed inset-0 z-[80]">
          <Splash />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** What the first screen needs before it can be drawn: who is signed in, and as which profile. */
const BOOT_QUERIES = new Set(['session', 'profile', 'profiles', 'platform-status']);

/**
 * Fades out the boot splash from index.html once the app's frame can be drawn: the router has its
 * page, the session and profile are known and the fonts are in. Films and shows load after it,
 * behind their skeletons, so the splash never waits on the catalogue. It never stays longer than
 * 8 seconds, so a slow request can't hold the app hostage.
 */
export function BootSplash() {
  const routing = useNavigation().state !== 'idle';
  // Only first loads count; background refreshes and polling never hold the splash.
  const fetching =
    useIsFetching({
      predicate: (q) => q.state.data === undefined && BOOT_QUERIES.has(String(q.queryKey[0])),
    }) > 0;
  const done = useRef(false);
  const [fonts, setFonts] = useState(false);

  useEffect(() => {
    if (document.fonts) void document.fonts.ready.then(() => setFonts(true));
    else setFonts(true);
    const cap = setTimeout(hideBootSplash, 8000);
    return () => clearTimeout(cap);
  }, []);

  useEffect(() => {
    if (done.current || routing || fetching || !fonts) return;
    // Wait a beat so queries that start right after the first render are counted too.
    const t = setTimeout(() => {
      done.current = true;
      hideBootSplash();
    }, 60);
    return () => clearTimeout(t);
  }, [routing, fetching, fonts]);

  return null;
}

/** Long enough for the logo's entrance, so a fast start doesn't flash. */
const MIN_BOOT_MS = 600;

export function hideBootSplash() {
  const el = document.getElementById('boot-splash');
  if (!el || el.classList.contains('done')) return;
  const wait = Math.max(0, MIN_BOOT_MS - performance.now());
  setTimeout(() => {
    el.classList.add('done');
    setTimeout(() => el.remove(), 600);
  }, wait);
}
